import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  ArrowLeftIcon, 
  DocumentTextIcon, 
  ListBulletIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline'
import normatividadService from '../../../services/normatividadService'
import { TemaNormativo } from '../../../types/normatividad'
import { usePermisos } from '../../../ganchos/usePermisos'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'font': [] }],
    [{ 'align': [] }],
    ['clean'],
    ['link', 'image', 'video']
  ],
}

// Tipos de formulario
type FormValues = {
  id: string
  titulo: string
  descripcion: string
  tipo: string
  categoria: string
  actualizado: string
  contenido: string
  indiceJson: string // Para editar el índice como JSON
}

const PaginaEditorNormativa: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { tienePermiso } = usePermisos()
  const isEditing = !!id

  useEffect(() => {
    if (isEditing && !tienePermiso('normativa:editar')) {
      navigate('/admin/normatividad')
      return
    }
    if (!isEditing && !tienePermiso('normativa:crear')) {
      navigate('/admin/normatividad')
      return
    }
  }, [isEditing, tienePermiso, navigate])

  const { register, handleSubmit, setValue, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      tipo: 'LEY',
      categoria: 'comercio_exterior',
      actualizado: new Date().getFullYear().toString(),
      indiceJson: '[]',
      contenido: ''
    }
  })

  useEffect(() => {
    const cargarNorma = async () => {
      if (isEditing && id) {
        const norma = await normatividadService.getNormaById(id)
        if (norma) {
          setValue('id', norma.id)
          setValue('titulo', norma.titulo)
          setValue('descripcion', norma.descripcion)
          setValue('tipo', norma.tipo)
          setValue('categoria', norma.categoria)
          setValue('actualizado', norma.actualizado)
          setValue('contenido', norma.contenido || '')
          setValue('indiceJson', JSON.stringify(norma.indice || [], null, 2))
        } else {
          alert('Normativa no encontrada')
          navigate('/admin/normatividad')
        }
      }
    }
    cargarNorma()
  }, [id, isEditing, navigate, setValue])

  const onSubmit = async (data: FormValues) => {
    try {
      // Parsear índice JSON
      let indiceParsed = []
      try {
        indiceParsed = JSON.parse(data.indiceJson)
      } catch (e) {
        alert('El formato del índice JSON es inválido.')
        return
      }

      const nuevaNorma: TemaNormativo = {
        id: isEditing ? data.id : (data.id || crypto.randomUUID()), // Generar ID si es nuevo y no se proveyó (aunque el campo id está en form)
        titulo: data.titulo,
        descripcion: data.descripcion,
        tipo: data.tipo as any,
        categoria: data.categoria as any,
        actualizado: data.actualizado,
        contenido: data.contenido,
        indice: indiceParsed
      }

      await normatividadService.saveNorma(nuevaNorma)
      navigate('/admin/normatividad')
    } catch (error) {
      console.error(error)
      alert('Error al guardar la normativa.')
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/normatividad')}
          className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Editar Normativa' : 'Nueva Normativa'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isEditing ? 'Modifica los detalles del documento legal.' : 'Registra un nuevo documento en la base de datos.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        
        {/* Sección General */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-6">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
            <DocumentTextIcon className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Información General</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título del Documento</label>
              <input
                {...register('titulo', { required: 'El título es obligatorio' })}
                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Ej. Ley Aduanera"
              />
              {errors.titulo && <span className="text-red-500 text-xs mt-1">{errors.titulo.message}</span>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción Breve</label>
              <textarea
                {...register('descripcion', { required: 'La descripción es obligatoria' })}
                rows={2}
                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Resumen del propósito de la norma..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ID (Identificador Único)</label>
              <input
                {...register('id', { required: 'El ID es obligatorio', pattern: /^[a-z0-9-]+$/ })}
                disabled={isEditing}
                className={`w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 ${isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
                placeholder="ej. ley-aduanera-2024"
              />
              <p className="text-xs text-gray-500 mt-1">Sin espacios, usa guiones. Ej: ley-aduanera</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado / Actualización</label>
              <input
                {...register('actualizado')}
                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Ej. Vigente, 2024, Reformado"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Documento</label>
              <select
                {...register('tipo')}
                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="LEY">LEY</option>
                <option value="REGLAMENTO">REGLAMENTO</option>
                <option value="TRATADO">TRATADO</option>
                <option value="REGLA">REGLA</option>
                <option value="DECRETO">DECRETO</option>
                <option value="MANUAL">MANUAL</option>
                <option value="NOM">NOM</option>
                <option value="ACUERDO">ACUERDO</option>
                <option value="GUIA">GUIA</option>
                <option value="CIRCULAR">CIRCULAR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoría</label>
              <select
                {...register('categoria')}
                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="comercio_exterior">Comercio Exterior</option>
                <option value="fiscal">Fiscal</option>
                <option value="clasificacion">Clasificación / Tarifa</option>
                <option value="seguridad">Seguridad / NOMs</option>
                <option value="puertos">Puertos y Logística</option>
                <option value="otros">Otros</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sección Contenido */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-6">
          <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <CodeBracketIcon className="h-5 w-5 text-purple-500" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Contenido del Documento</h2>
            </div>
            <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">Editor Avanzado</span>
          </div>
          
          <div className="prose-editor-container">
            <Controller
              name="contenido"
              control={control}
              render={({ field }) => (
                <ReactQuill 
                  theme="snow" 
                  value={field.value} 
                  onChange={field.onChange} 
                  modules={modules}
                  className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg"
                />
              )}
            />
            <p className="mt-2 text-xs text-gray-500">
              Utiliza las herramientas de formato para estructurar el documento. El contenido se guardará automáticamente como HTML formateado.
            </p>
            <style>{`
              .ql-toolbar {
                border-top-left-radius: 0.5rem;
                border-top-right-radius: 0.5rem;
                background-color: #f3f4f6;
              }
              .dark .ql-toolbar {
                background-color: #374151;
                border-color: #4b5563;
                color: white;
              }
              .dark .ql-stroke {
                stroke: #e5e7eb !important;
              }
              .dark .ql-fill {
                fill: #e5e7eb !important;
              }
              .dark .ql-picker {
                color: #e5e7eb !important;
              }
              .ql-container {
                border-bottom-left-radius: 0.5rem;
                border-bottom-right-radius: 0.5rem;
                font-size: 16px;
                min-height: 400px;
              }
              .dark .ql-container {
                border-color: #4b5563;
              }
              .ql-editor {
                min-height: 400px;
              }
            `}</style>
          </div>
        </div>

        {/* Sección Índice */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-6">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
            <ListBulletIcon className="h-5 w-5 text-green-500" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Estructura del Índice (JSON)</h2>
          </div>
          
          <div>
            <textarea
              {...register('indiceJson')}
              rows={6}
              className="w-full font-mono text-sm rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder='[{"titulo": "Sección 1", "id_referencia": "sec1", "nivel": 1}]'
            />
            <p className="mt-2 text-xs text-gray-500">
              Formato JSON array. Ejemplo: <code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">[{`{ "titulo": "Intro", "id_referencia": "intro", "nivel": 1 }`}]</code>
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/normatividad')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Guardar Cambios
          </button>
        </div>
      </form>
    </div>
  )
}

export default PaginaEditorNormativa
